/* eslint-disable react/prop-types */
'use client';

import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { STORAGE_KEYS } from '../../constant/constant';
import { useNavigate } from 'react-router-dom';
import { useCategoryIcons } from '../../contexts/CategoryIconContext';

// ====================================================================================
// Data Helper Functions
// ====================================================================================

function getName(node, fieldNames) {
  const keys = fieldNames?.name || ['name', 'categoryName', 'title'];
  for (const k of keys) {
    if (node && node[k]) return node[k];
  }
  return 'Category';
}

function getChildren(node, fieldNames) {
  const keys = fieldNames?.children || ['subCategories', 'children', 'items'];
  for (const k of keys) {
    if (Array.isArray(node?.[k])) return node[k];
  }
  return [];
}

// ====================================================================================
// Image Path Helper
// ====================================================================================

function getImagePath(name, level, getIconUrl) {
  const sanitizedName = name.replace(/\s*&\s*/g, ' & ').replace(/\s+/g, ' ');
  return getIconUrl(sanitizedName);
}

// ====================================================================================
// Main Component: CategoryTree
// ====================================================================================

export default function CategoryTree({ categories = [], fieldNames }) {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const getIconUrl = useCategoryIcons();

  // Search logic finds all matching categories at any level and flattens them for display
  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];

    const results = [];
    const findMatches = (nodes, level, parent = null) => {
      // Stop searching after the second level (level 0 and 1)
      if (level >= 2) {
        return;
      }

      nodes.forEach((node) => {
        if (getName(node, fieldNames).toLowerCase().includes(q)) {
          results.push({ ...node, level, parentNode: parent });
        }
        const children = getChildren(node, fieldNames);
        if (children.length > 0) {
          findMatches(children, level + 1, node);
        }
      });
    };

    findMatches(categories, 0, null);
    // Ensure unique results if categories have duplicate names
    return Array.from(new Map(results.map((item) => [getName(item, fieldNames), item])).values());
  }, [query, categories, fieldNames]);

  const handleCategoryClick = (node, parentNode) => {
    sessionStorage.setItem(STORAGE_KEYS.ACTIVE_CATEGORY_ID, parentNode?.id);
    sessionStorage.setItem(STORAGE_KEYS.ACTIVE_SUB_CATEGORY_ID, node?.id);

    navigate('/store');
  };

  const isSearching = query.trim().length > 0;

  return (
    <div className="w-full max-w-8xl mx-auto py-3 px-2 sm:py-4 sm:px-4 bg-slate-50 font-sans">
      <div className="flex flex-col sm:flex-row items-center justify-between mb-2 gap-3">
        <h2 className="text-xl font-bold text-slate-900 tracking-tight"></h2>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-slate-400 pointer-events-none" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search categories..."
            className="h-11 w-full rounded-full border border-slate-300 bg-white pl-11 pr-4 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-500 transition"
          />
        </div>
      </div>

      {isSearching ? (
        <div>
          <h3 className="mb-5 text-xl font-semibold text-slate-700">Search Results</h3>
          <GridView
            nodes={searchResults}
            onCategoryClick={(node) => handleCategoryClick(node, node.parentNode)}
            fieldNames={fieldNames}
            isSearching={true}
            getIconUrl={getIconUrl}
          />
        </div>
      ) : (
        <div className="space-y-4">
          {categories.map((categoryNode) => (
            <CategoryGroup
              key={getName(categoryNode, fieldNames)}
              categoryNode={categoryNode}
              fieldNames={fieldNames}
              onCategoryClick={handleCategoryClick}
              getIconUrl={getIconUrl}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ====================================================================================
// Sub-Components
// ====================================================================================

function CategoryGroup({ categoryNode, fieldNames, onCategoryClick, getIconUrl }) {
  const name = getName(categoryNode, fieldNames);
  const children = getChildren(categoryNode, fieldNames);

  // Don't render a group if it has no sub-categories to show
  if (!children || children.length === 0) {
    return null;
  }

  return (
    <section>
      <div className="mb-2">
        <h3 className="text-2xl font-bold text-slate-800 tracking-tight">{name}</h3>
      </div>
      <GridView
        nodes={children}
        level={1} // All children in a group are considered level 1
        fieldNames={fieldNames}
        onCategoryClick={(childNode) => onCategoryClick(childNode, categoryNode)}
        getIconUrl={getIconUrl}
      />
    </section>
  );
}

function GridView({ nodes, level, fieldNames, onCategoryClick, isSearching = false, getIconUrl }) {
  if (nodes.length === 0) {
    return (
      <div className="text-center py-20 px-4 text-slate-500 bg-white rounded-xl border-2 border-dashed">
        <p className="font-semibold text-lg text-slate-700">No Categories Found</p>
        <p className="text-sm mt-2 max-w-xs mx-auto">
          {isSearching
            ? "We couldn't find any categories matching your search. Please try a different term."
            : 'There are no sub-categories to display here at the moment.'}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-9 gap-4 sm:gap-5">
      {nodes.map((node) => (
        <CategoryCard
          key={getName(node, fieldNames)}
          node={node}
          // Use the node's own level for search results, otherwise use the group's level
          level={isSearching ? node.level : level}
          fieldNames={fieldNames}
          onClick={() => onCategoryClick(node)}
          getIconUrl={getIconUrl}
        />
      ))}
    </div>
  );
}

function CategoryCard({ node, level, fieldNames, onClick, getIconUrl }) {
  const name = getName(node, fieldNames);
  const imageUrl = getImagePath(name, level, getIconUrl);

  return (
    <div
      onClick={onClick}
      className="group flex flex-col items-center text-center cursor-pointer transition-transform duration-300 hover:-translate-y-1"
    >
      <div className="relative w-full overflow-hidden rounded-lg bg-white border border-slate-200/80 shadow-sm transition-all duration-300 group-hover:shadow-md group-hover:border-emerald-300">
        <img
          src={imageUrl}
          alt={name}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://placehold.co/200x200/e2e8f0/64748b?text=Image';
          }}
          className="aspect-square w-full object-contain transition-transform duration-300 group-hover:scale-105"
        />
      </div>
      <h3 className="mt-2 text-xs sm:text-sm font-medium text-slate-700 transition-colors group-hover:text-emerald-600 px-1">
        {name}
      </h3>
    </div>
  );
}
